import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router'
import { apiPost } from '../../lib/api'
import type { Chat } from '../../lib/types'
import { BubblePanel } from './BubblePanel'
import './chat.css'

// Burbuja flotante de chat (esquina inferior derecha, presente en toda la app salvo
// /chat). El hexágono despliega un panel anclado que reusa la bandeja + Conversation.
// Disciplina de listeners (realtime-y-reglas.md): CERO listeners con el panel cerrado —
// subscribeChats/subscribeChatReads viven en BubblePanel, que solo se monta con open=true.
// En /chat la burbuja no se pinta para no DUPLICAR los listeners de esa página.

type BubbleView = { kind: 'list' } | { kind: 'conversation'; chatId: string }

interface BubbleCtx {
  open: boolean
  view: BubbleView
  // Doc de chat sembrado por openDm/openChatDoc: evita esperar a que el listener de la
  // bandeja lo alcance (mismo truco que el fallbackChat de la página /chat).
  seedChat: Chat | null
  dmPending: boolean
  error: string | null
  clearError: () => void
  openDm: (email: string) => void
  openChat: (chatId: string) => void
  openChatDoc: (chat: Chat) => void
  showList: () => void
  close: () => void
  toggle: () => void
}

const Ctx = createContext<BubbleCtx | null>(null)

export function ChatBubbleProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<BubbleView>({ kind: 'list' })
  const [seedChat, setSeedChat] = useState<Chat | null>(null)
  const [dmPending, setDmPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Evita crear dos DMs si se hace doble click en una tarjeta del directorio.
  const dmBusy = useRef(false)

  const clearError = useCallback(() => setError(null), [])

  const openChat = useCallback((chatId: string) => {
    setError(null)
    setSeedChat(null)
    setView({ kind: 'conversation', chatId })
    setOpen(true)
  }, [])

  const openChatDoc = useCallback((chat: Chat) => {
    setError(null)
    setSeedChat(chat)
    setView({ kind: 'conversation', chatId: chat.id })
    setOpen(true)
  }, [])

  // Crea/obtiene el DM por API (id determinístico, no duplica) y abre el panel en él.
  const openDm = useCallback((email: string) => {
    if (dmBusy.current) return
    dmBusy.current = true
    setError(null)
    setDmPending(true)
    setOpen(true)
    setView({ kind: 'list' })
    void apiPost<{ chat: Chat }>('/api/chats', { type: 'dm', memberEmail: email })
      .then(({ chat }) => {
        setSeedChat(chat)
        setView({ kind: 'conversation', chatId: chat.id })
      })
      .catch(() => setError('No se pudo abrir la conversación'))
      .finally(() => {
        setDmPending(false)
        dmBusy.current = false
      })
  }, [])

  const showList = useCallback(() => {
    setSeedChat(null)
    setView({ kind: 'list' })
  }, [])

  const close = useCallback(() => setOpen(false), [])

  const toggle = useCallback(() => setOpen((o) => !o), [])

  const value = useMemo<BubbleCtx>(
    () => ({
      open,
      view,
      seedChat,
      dmPending,
      error,
      clearError,
      openDm,
      openChat,
      openChatDoc,
      showList,
      close,
      toggle,
    }),
    [open, view, seedChat, dmPending, error, clearError, openDm, openChat, openChatDoc, showList, close, toggle],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useChatBubble(): BubbleCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useChatBubble debe usarse dentro de <ChatBubbleProvider>')
  return ctx
}

// El panel sigue montado --dur-1 (120ms) DESPUÉS de cerrarse, para poder salir con
// una animación en vez de desaparecer de golpe. Es la única ventana en la que hay
// listeners con el chat "cerrado" y dura lo que dura la salida; la disciplina de
// realtime-y-reglas.md (cero listeners en reposo) se conserva.
const MS_SALIDA = 120

export function ChatBubble() {
  const bubble = useChatBubble()
  const { pathname } = useLocation()
  // El hexágono no cuenta como "fuera": sin esta excepción un clic en él cerraría
  // por el listener del panel y volvería a abrir por su propio toggle (§3.8).
  const hexRef = useRef<HTMLButtonElement>(null)
  const [saliendo, setSaliendo] = useState(false)
  const abiertoAntes = useRef(bubble.open)

  useEffect(() => {
    const seCerro = abiertoAntes.current && !bubble.open
    abiertoAntes.current = bubble.open
    if (bubble.open) {
      setSaliendo(false)
      return
    }
    if (!seCerro) return
    setSaliendo(true)
    const t = window.setTimeout(() => setSaliendo(false), MS_SALIDA)
    return () => window.clearTimeout(t)
  }, [bubble.open])

  // En /chat la vista amplia ya monta sus propios listeners: no dupliquemos la burbuja.
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return null

  return (
    <>
      <button
        ref={hexRef}
        type="button"
        className={`chat-hex${bubble.open ? ' is-open' : ''}`}
        onClick={bubble.toggle}
        aria-label={bubble.open ? 'Cerrar chat' : 'Abrir chat'}
        aria-expanded={bubble.open}
      >
        {/* Los DOS iconos viven siempre en el árbol, superpuestos: el cambio globo↔X
            es un cross-fade de --dur-1, no un cambiazo seco (§3.8). */}
        <span className="chat-hex-ico" aria-hidden="true">
          <svg className="chat-hex-globo" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <svg className="chat-hex-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </span>
      </button>
      {/* BubblePanel solo se monta con el panel abierto (más los 120ms de salida)
          → cero listeners con el chat en reposo. */}
      {(bubble.open || saliendo) && <BubblePanel hexRef={hexRef} saliendo={saliendo} />}
    </>
  )
}
